import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Divider,
  Radio,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import type {
  Filter,
  FollowUpAction,
  Inspiration,
  InspirationContent,
} from 'src/types/myFollowUp';
import { KPI_DISPLAY_CUTOFF } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/constants';
import useFollowUpDetail from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

import { styles } from './index.styles';

export const ACTION_TITLE_FORM_FIELD_TEST_ID = 'action-title-form-field';
export const ACTION_DETAILS_FORM_FIELD_TEST_ID = 'action-details-form-field';
export const ADDITIONAL_RESOURCES_FORM_FIELD_TEST_ID =
  'additional-resources-form-field';

export interface ActionPlanContent {
  title?: string;
  details?: string;
  additionalResources?: string;
}

export interface ActionPlanProps {
  actionItem?: FollowUpAction | undefined;
  kpi?: Filter | undefined;
  onContentChange: (content: ActionPlanContent) => void;
  disabled?: boolean;
}

const ActionPlan: FC<ActionPlanProps> = ({
  actionItem,
  kpi,
  onContentChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('myFollowUp');
  const { mutationError } = useFollowUpDetail();

  const [selectedInspiration, setSelectedInspiration] = useState<number | null>(
    null,
  );
  const [isOwnPlanSelected, setIsOwnPlanSelected] =
    useState<boolean>(!!actionItem);
  const [actionTitle, setActionTitle] = useState<string>(
    actionItem?.actionPlanTitle || '',
  );
  const [actionDetails, setActionDetails] = useState<string>(
    actionItem?.actionPlanDetails || '',
  );
  const [additionalResources, setAdditionalResources] = useState<string>(
    actionItem?.actionPlanAdditionalResources || '',
  );

  const handleSelectionChange = (inspiration: Inspiration) => {
    setSelectedInspiration(inspiration.id);
    setIsOwnPlanSelected(false);

    const content = inspiration.content as InspirationContent;
    setActionTitle(content?.title || `Inspiration ${inspiration.id}`);

    // Formats a list of items into a string, each item on a new line prefixed with '- '.
    // If an item has a link, it appends the link in parentheses.
    // Example input:
    // [
    //   { text: 'Example bullet point one', link: 'https://getreport.com' },
    //   { text: 'Example bullet point two', link: null },
    //   { text: 'Example bullet point three', link: null }
    // ]
    // Output:
    // - Example bullet point one (https://getreport.com)
    // - Example bullet point two
    // - Example bullet point three
    const formatListItems = (
      items?: { link: string | null; text: string }[] | undefined,
    ) =>
      items
        ?.map((item) => {
          let text = `- ${item.text}` || '';
          if (item.link) {
            text = `${text} (${item.link})`;
          }
          return text;
        })
        .join('\n') || '';

    const actionDetailsText = formatListItems(
      content?.implementationOfAction?.list,
    );

    setActionDetails(actionDetailsText);

    const resourcesText = formatListItems(content?.furtherSupport?.list);
    setAdditionalResources(resourcesText);
  };

  const handleOwnPlanSelection = () => {
    setIsOwnPlanSelected(true);
    setSelectedInspiration(null);
    setActionTitle(actionItem?.actionPlanTitle || '');
    setActionDetails(actionItem?.actionPlanDetails || '');
    setAdditionalResources(actionItem?.actionPlanAdditionalResources || '');
  };

  const getInspirationDescription = (inspiration: Inspiration): string => {
    const content = inspiration.content as InspirationContent;
    return content?.title || '';
  };

  useEffect(() => {
    onContentChange({
      title: actionTitle,
      details: actionDetails,
      additionalResources: additionalResources,
    });
  }, [actionTitle, actionDetails, additionalResources, onContentChange]);

  const renderExpandedContent = () => (
    <Box sx={styles.expandedContent}>
      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t('actionPlan.actionTitle')}
        </Typography>
        <TextField
          disabled={disabled}
          fullWidth
          placeholder={t('actionPlan.titlePlaceholder')}
          value={actionTitle}
          onChange={(e) => setActionTitle(e.target.value)}
          sx={styles.textField}
          inputProps={{
            'data-testid': ACTION_TITLE_FORM_FIELD_TEST_ID,
            'data-test-id': ACTION_TITLE_FORM_FIELD_TEST_ID,
          }}
        />
      </Box>
      {mutationError?.actionPlanTitle && (
        <Box sx={styles.error}>{mutationError.actionPlanTitle}</Box>
      )}
      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t('actionPlan.actionDetails')}
        </Typography>
        <TextField
          disabled={disabled}
          fullWidth
          multiline
          rows={4}
          value={actionDetails}
          onChange={(e) => setActionDetails(e.target.value)}
          sx={styles.textField}
          inputProps={{
            'data-testid': ACTION_DETAILS_FORM_FIELD_TEST_ID,
            'data-test-id': ACTION_DETAILS_FORM_FIELD_TEST_ID,
          }}
        />
        {mutationError?.actionPlanDetails && (
          <Box sx={styles.error}>{mutationError.actionPlanDetails}</Box>
        )}
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t('actionPlan.actionAdditionalResources')}
        </Typography>
        <TextField
          disabled={disabled}
          fullWidth
          multiline
          rows={4}
          value={additionalResources}
          onChange={(e) => setAdditionalResources(e.target.value)}
          sx={styles.textField}
          inputProps={{
            'data-testid': ADDITIONAL_RESOURCES_FORM_FIELD_TEST_ID,
            'data-test-id': ADDITIONAL_RESOURCES_FORM_FIELD_TEST_ID,
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={styles.container}>
      <Divider sx={styles.divider} />
      <Typography variant="h6" sx={styles.title}>
        {t('actionPlan.actionPlan')}
      </Typography>
      <Stack spacing={3}>
        <Box>
          <Box sx={styles.itemContainer}>
            <Box onClick={disabled ? undefined : handleOwnPlanSelection}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'flex-start' }}
              >
                <Radio
                  disabled={disabled}
                  checked={isOwnPlanSelected}
                  onChange={handleOwnPlanSelection}
                  sx={styles.radio(theme, isOwnPlanSelected)}
                />
                <Box sx={styles.contentContainer}>
                  <Typography variant="body3">
                    {t('actionPlan.ownPlan')}
                  </Typography>
                  {!isOwnPlanSelected && (
                    <Typography variant="body2">
                      {t('actionPlan.createOwnActionPlan')}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>

            {isOwnPlanSelected && renderExpandedContent()}
          </Box>
        </Box>

        {kpi?.indicator1 !== null && // Only show inspirations if KPI has valid indicator value
          kpi?.inspirations.map((inspiration, index) => {
            const isSelected = selectedInspiration === inspiration.id;

            return (
              <Box key={inspiration.id}>
                <Box sx={styles.itemContainer}>
                  <Box
                    onClick={
                      disabled
                        ? () => null
                        : () => handleSelectionChange(inspiration)
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <Radio
                        disabled={disabled}
                        checked={isSelected}
                        onChange={() => handleSelectionChange(inspiration)}
                        sx={styles.radio(theme, isSelected)}
                      />
                      <Box sx={styles.contentContainer}>
                        <Typography variant="body3">
                          {t('followUpInspirations:inspiration')} {index + 1}
                        </Typography>
                        {!isSelected && (
                          <Typography variant="body2">
                            {getInspirationDescription(inspiration)}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={styles.progressContainer}>
                        <Box sx={styles.progressInfo}>
                          <Typography sx={styles.progressText}>
                            {inspiration.expectedOutcome >= KPI_DISPLAY_CUTOFF
                              ? `≥ ${KPI_DISPLAY_CUTOFF}p`
                              : `+ ${inspiration.expectedOutcome.toFixed(1)}p`}
                          </Typography>
                          <span title={t('inspirationInfoText')}>
                            <InfoOutlinedIcon sx={styles.infoIcon} />
                          </span>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>

                  {isSelected && renderExpandedContent()}
                </Box>
              </Box>
            );
          })}
      </Stack>
    </Box>
  );
};

export default ActionPlan;
